import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  Grid,
  CircularProgress,
  Alert,
  // Tooltip, // UNUSED
} from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import WarningIcon from '@mui/icons-material/Warning';
import { DashboardService } from '../../../service/dashboard.service';
import { PlcService } from '../../../service/plc.service';
// ENTFERNT: import { SettingsDeploymentService } from '../../../service/settings-deployment.service';

const PLCSettingsView: React.FC = () => {
  const [plcUrl, setPlcUrl] = useState('');
  const [plcStatus, setPlcStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [tempPlcUrl, setTempPlcUrl] = useState('');
  const [testInProgress, setTestInProgress] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  useEffect(() => {
    fetchPlcData();
  }, []);

  const fetchPlcData = async () => {
    try {
      setLoading(true);
      const data = await DashboardService.getDashboard();
      
      setPlcUrl(data.plcUrl || '');
      setTempPlcUrl(data.plcUrl || '');
      setPlcStatus(data.plcUrl ? 'online' : 'offline');
    } catch (error) {
      console.error('Fehler beim Abrufen der PLC-Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setTempPlcUrl(plcUrl);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleCancel = () => {
    setEditMode(false);
    setTempPlcUrl(plcUrl);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaveError(null);
      setSaveSuccess(false);
      
      // IP-Adresse validieren
      if (tempPlcUrl && !/^(\d{1,3}\.){3}\d{1,3}$/.test(tempPlcUrl)) {
        setSaveError('Bitte geben Sie eine gültige IP-Adresse ein (z.B. 192.168.1.100)');
        return;
      }
      
      // API-Aufruf zum Setzen der PLC-URL
      await PlcService.postPlc({ url: tempPlcUrl });
      
      // Update local state to reflect changes
      setPlcUrl(tempPlcUrl);
      setPlcStatus(tempPlcUrl ? 'online' : 'offline');
      setEditMode(false);
      setSaveSuccess(true);
      
      // ENTFERNT: Löse Deployment aus
      // await SettingsDeploymentService.triggerDeploymentAfterSettingsChange('PLC-Einstellungen');
      
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Fehler beim Speichern der PLC-URL:', error);
      setSaveError('Die PLC-URL konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.');
    }
  };

  const handleTestCommand = async () => {
    if (!plcUrl) {
      return;
    }
    
    try {
      setTestInProgress(true);
      setPlcStatus('Test läuft...');
      setSaveError(null);
      
      await PlcService.sendTestCommand();
      
      setPlcStatus('Test erfolgreich');
      setTimeout(() => {
        setPlcStatus('online');
        setTestInProgress(false);
      }, 3000);
    } catch (error) {
      console.error("Fehler beim Senden des Testauslösen-Befehls:", error);
      
      setPlcStatus('Test fehlgeschlagen');
      setTimeout(() => {
        setPlcStatus('online');
        setTestInProgress(false);
      }, 3000);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <MemoryIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" component="h1">
          Waschstraßensteuerung (PLC)
        </Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">PLC-Einstellungen</Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {editMode ? (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="PLC URL"
                value={tempPlcUrl}
                onChange={(e) => setTempPlcUrl(e.target.value)}
                margin="normal"
                placeholder="z.B. 192.168.1.100"
                helperText="IP-Adresse zur Steuerung der Waschstraße"
                error={!!saveError}
              />
              
              {saveError && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                  {saveError}
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={handleCancel}>
                  Abbrechen
                </Button>
                <Button variant="contained" onClick={handleSave}>
                  Speichern
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">PLC URL</Typography>
                      <Typography variant="body1">
                        {plcUrl || 'Nicht konfiguriert'}
                      </Typography>
                    </Box>
                    <Button 
                      variant="outlined" 
                      onClick={handleEdit}
                      sx={{ 
                        color: 'black', 
                        borderColor: 'black',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          borderColor: 'black'
                        }
                      }}
                    >
                      Bearbeiten
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTestCommand}
                    disabled={!plcUrl || testInProgress || plcStatus === 'Test läuft...'}
                    sx={{ mr: 2 }}
                  >
                    {testInProgress ? 'Test läuft...' : 'Test ausführen'}
                  </Button>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    backgroundColor: '#fff3cd',
                    border: '2px solid #ffc107',
                    p: 1.5,
                    borderRadius: 2,
                    maxWidth: '70%',
                    boxShadow: '0 2px 4px rgba(255,193,7,0.3)'
                  }}>
                    <WarningIcon sx={{ color: '#d32f2f', mr: 1, fontSize: '1.5rem' }} />
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'bold', 
                      color: '#721c24',
                      fontSize: '0.875rem'
                    }}>
                      Achtung: Alarm setzen wird das Fließband der Waschstraße gestoppt!
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {saveSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  PLC-Einstellungen wurden erfolgreich gespeichert!
                </Alert>
              )}
              
              {plcStatus === 'Test erfolgreich' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Test war erfolgreich! Die PLC-Verbindung funktioniert.
                </Alert>
              )}
              
              {plcStatus === 'Test fehlgeschlagen' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Test ist fehlgeschlagen. Bitte überprüfen Sie die Verbindung.
                </Alert>
              )}
            </Box>
          )}

          {!plcUrl && !editMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Keine PLC-Steuerung konfiguriert. Klicken Sie auf "Bearbeiten", um eine URL einzurichten.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PLCSettingsView; 